import { useContext, useEffect, useState } from 'react'
import Head from 'next/head'
import Script from 'next/script'
import Image from 'next/image'
import { s3Static } from '../../../utils/constants'
import { getArticleDateTime, reviewedIso } from '../../../utils/article-date-time'
import { BlogsList } from '../../../components/blogs/BlogList'
import { blogsMaster } from '../../../utils/sg-blogs-data'
import { getRequestJSON } from '../../../api/index/location-api'
import { RequestProvider } from '../../../context/RequestContext'
import AirconRequestSection from '../../../components/index/service-category/aircon-service/AirconRequestSection'
import { AirconScope } from '../../../components/index/service-category/aircon-service/AirconScope'
import { AnimatePresence } from 'framer-motion'
import { airconCostCalculation } from '../../../js/aircon-cost'
import { getWpNewPostsByCategory, getWpPostsByCategory } from '../../../lib/service'
import { getWpHvacServices, getWpNewHvacServices, setWpHvacServices, setWpNewHvacServices } from '../../../service/wp-blogs-store'
import { buildAirconServiceGraph } from 'utils/aircon-page-schema'

// When this page's content last actually changed. Bump it by hand when you
// edit the copy - not for layout/UX changes. Feeds dateModified and
// article:modified_time (see reviewedIso in utils/article-date-time).
const CONTENT_REVIEWED = '2026-09-22'

export async function getStaticProps() {
    let lat = 1.3521
    let lng = 103.8198
    const { month, year } = getArticleDateTime();
    const modifiedDate = reviewedIso(CONTENT_REVIEWED);
    const airconBlogs = blogsMaster['hvac-services'].blogArray;
    const wpPosts = getWpHvacServices().length > 0 ?  getWpHvacServices() : await getWpPostsByCategory(6, "hvac-services");
    setWpHvacServices(wpPosts)
    const wpNewPosts = getWpNewHvacServices().length > 0 ?  getWpNewHvacServices() : await getWpNewPostsByCategory(6, "hvac-services");
    setWpNewHvacServices(wpNewPosts)

    const allRequestCategorySettings = await getRequestJSON("Singapore")
    const requestCategorySettings = allRequestCategorySettings.filter(category => category.requestType === "Aircon Services")

    const currencyCd = "SGD"
    let standardServicing = []
    let annualServicing = []
    let chemicalServicing = []
    let overhaulServicing = []
    airconCostCalculation(currencyCd, annualServicing, standardServicing, chemicalServicing, overhaulServicing)

  return {
      props: {
        requestCategorySettings,
        currencyCd,
        standardServicing,
        chemicalServicing,
        overhaulServicing,
        modifiedDate,
        month,
        year,
        airconBlogs,
        wpNewPosts,
        wpPosts
      },
      // Next.js will attempt to re-generate the page:
      // - When a request comes in
      // - At most once every 10 seconds
      revalidate: 259200, // In seconds
  }
}

export default function Home(props) {
    const countryName = "Singapore"
    const requestType = "Aircon Services"

    const [ requestForm, setRequestForm ] = useState(false)
    const [ isQuickBook, setIsQuickBook ] = useState(false)
    const [ isCustomBook, setIsCustomBook ] = useState(false)
    const [ isShowJobScope, setIsShowJobScope ] = useState(false)
    const [ serviceType, setServiceType ] = useState("general")
    const [ unit, setUnit ] = useState("")
    const [ unitType, setUnitType ] = useState("")
    const [ cost, setCost ] = useState("")
    const modifiedDate = props.modifiedDate
    const month = props.month
    const year = props.year
    const airconBlogs = props.airconBlogs
    const requestCategorySettings = props.requestCategorySettings;
    const wpPosts = props.wpPosts
    const wpNewPosts = props.wpNewPosts
    const currencyCd = props.currencyCd
    const standardServicing = props.standardServicing
    const chemicalServicing = props.chemicalServicing
    const overhaulServicing = props.overhaulServicing

    const maxRating = [1, 2, 3, 4, 5]

  return (
    <>
      <Head>
        <title>Aircon Servicing Near Me</title>
        <meta charSet="utf-8"/>
        <meta httpEquiv="X-UA-Compatible" content="IE=edge"/>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="description" content="How do I find aircon servicing near me? 1. Open a request on StringsSG. 2. Google search for a nearby aircon servicing companies."/>
        <meta name="author" content="StringsSG"/>
        <meta name="google-site-verification" content="JAjj5vAc8Xxrh4idRAchZhYR6rwxWPAk3vxMdx-dwVQ" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:url"   content="https://www.stringssg.com/sg/aircon-servicing-singapore/aircon-servicing-near-me" />
        <meta property="og:type"   content="article" />
        <meta property="og:title"    content={`Aircon Servicing Near Me`}/>
        <meta property="og:description" content="How do I find aircon servicing near me? 1. Open a request on StringsSG. 2. Google search for a nearby aircon servicing companies." />
        <meta property="article:publisher" content="https://www.facebook.com/StringsSG/" />
        <meta property="article:published_time" content="2025-01-28T07:30:46+00:00" />
        <meta property="article:modified_time" content={modifiedDate} />
        <meta property="og:image"  content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/aircon-front.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/aircon-front.jpg" />
        <meta name="twitter:site" content="@stringssg"/> 
        <meta name="twitter:url" content="https://www.stringssg.com/sg/aircon-servicing-singapore/aircon-servicing-near-me"/>
        <meta name="twitter:title" content={`Aircon Servicing Near Me`}/> 
        <meta name="twitter:description" content="How do I find aircon servicing near me? 1. Open a request on StringsSG. 2. Google search for a nearby aircon servicing companies."/>
        <link rel="icon" href="/favicon.ico" />
        <link rel="canonical" href="https://www.stringssg.com/sg/aircon-servicing-singapore/aircon-servicing-near-me"/>
        <script type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(buildAirconServiceGraph({
            path: '/sg/aircon-servicing-singapore/aircon-servicing-near-me',
            pageName: 'Aircon Servicing Near Me',
            description: 'How do I find aircon servicing near me? 1. Open a request on StringsSG. 2. Google search for a nearby aircon servicing companies.',
            serviceName: 'Aircon Servicing Near Me',
            serviceType: 'Aircon Servicing',
            image: 'https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/aircon-front.jpg',
            datePublished: '2025-01-28T07:30:46+00:00',
            modifiedDate,
          })) }}
        />
        </Head>
        <div>
            {/* Hero H1 lives in AirconBooking (via AirconRequestSection); the body
                heading below is an H2. This replaced AirconServiceRequestForm, which
                auto-opened the question wizard on page load. */}
            <RequestProvider>
                <AirconRequestSection
                    requestForm={requestForm}
                    setRequestForm={setRequestForm}
                    isQuickBook={isQuickBook}
                    setIsQuickBook={setIsQuickBook}
                    isCustomBook={isCustomBook}
                    setIsCustomBook={setIsCustomBook}
                    requestCategorySettings={requestCategorySettings}
                    standardServicing={standardServicing}
                    chemicalServicing={chemicalServicing}
                    overhaulServicing={overhaulServicing}
                    currencyCd={currencyCd}
                    countryName={countryName}
                    requestType={requestType}
                    setIsShowJobScope={setIsShowJobScope}
                    serviceType={serviceType}
                    setServiceType={setServiceType}
                    setUnit={setUnit}
                    setUnitType={setUnitType}
                    setCost={setCost}
                    heroImage="aircon-front.jpg"
                    title="Find Aircon Servicing Near You"
                />
            </RequestProvider>
            <div className='max-w-5xl mx-auto py-5 px-2 text-sm text-gray-500 leading-7'>
                <div className='space-y-2'>
                    <h2 className='text-lg text-gray-500 font-semibold'>Find Aircon Servicing Near You</h2>
                    <div className='text-gray-400 text-xs'>
                        <time dateTime={modifiedDate}>Updated - {new Date(modifiedDate).toUTCString().substring(0,16)}</time>
                    </div>
                </div>
                <div className='py-2 space-y-2'>
                    <div className='py-2 space-y-5'>
                        <p className=''>
                            Almost every household in Singapore uses aircon because of the hot weather, so the the demand for air con repair and servicing is very high. Which also means
                            there are a lot of aircon servicing companies in Singapore. 
                        </p>
                        <div className='py-2 space-y-2'>
                            <h2 className='text-base font-bold'><u>How to find aircon servicing near me?</u></h2>
                            <div className='space-y-2'>
                                <p>
                                    <strong>StringsSG - </strong>StringsSG has a huge database of aircon servicing companies in almost every location in Singapore. All you need to do is open a request
                                    and we will connect you to the most relevant service provider.          
                                </p>
                                <p>
                                    <strong>Google - </strong>Jump onto Google to start searching and browsing aircon servicing companies near you.
                                </p>
                                <p>
                                    <strong>Referral - </strong>Sometimes our friends and relatives may know of a trust worthy servicing company that they can recommend.
                                </p>
                                <p>
                                    <strong>Near By Aircon Servicing Company - </strong>Walk-in to their office and request for a service or just get the quotes.
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className='relative h-72 max-w-md'>
                        <Image className='rounded-lg'
                            src={s3Static + "aircon-front.jpg"}
                            fill
                            style={{objectFit: 'contain'}}
                            placeholder='blur'
                            blurDataURL={s3Static + "placeholder.png"}
                            alt='aircon front'
                        />          
                    </div>
                    <div>
                        <div className='space-y-2 py-2'>
                            <h2 className='text-base font-bold'><u>What should you be looking for in aircon servicing companies</u></h2>
                            <p>
                                <strong>Budget Friendly - </strong>The servicing cost should be within the standard range in Singapore. If unsure of the cost estimation, Click on 
                                &apos;Get Aircon Service&apos; to get an estimation.
                            </p>
                            <p>
                                <strong>Communication - </strong>It is important that your service provider proactively responds to your enquiries and provides a mechanism to keep track
                                of the agreement and conversations.
                            </p>
                            <p>
                                <strong>Reliability - </strong>Most of us do not understand the work that is being carried out during an air con repair, servicing or gas refill. So we 
                                completely rely on the service provider to be honestly carrying out their duties and providing us truthful information.
                            </p>
                            <p>
                                <strong>Dispute Resolution - </strong>Your service provider must proactively resolve any problems that is caused by the servicing or if the repair is 
                                not working as expected.
                            </p>
                        </div>
                    </div>  
                </div> 
                <BlogsList blogs={airconBlogs} wpPosts={wpPosts} wpNewPosts={wpNewPosts}/> 
            </div>
        </div>
        {isShowJobScope &&
            <AnimatePresence>
                <AirconScope
                setIsShowJobScope={setIsShowJobScope}
                serviceType={serviceType}
                unit={unit}
                unitType={unitType}
                cost={cost} />
            </AnimatePresence>
        }
        <Script 
          id="google-tagmanager-new"
          src="https://www.googletagmanager.com/gtag/js?id=G-QD1HGGHDYJ"
          strategy="lazyOnload"
          onLoad={() => {              
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
          
            gtag('config', 'G-QD1HGGHDYJ');
          }} 
      />
      <Script id="facebook-tracking" strategy="lazyOnload">
          {`!function(f,b,e,v,n,t,s)
					  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
					  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
					  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
					  n.queue=[];t=b.createElement(e);t.async=!0;
					  t.src=v;s=b.getElementsByTagName(e)[0];
					  s.parentNode.insertBefore(t,s)}(window, document,'script',
					  'https://connect.facebook.net/en_US/fbevents.js');
					  fbq('init', '128634041116395');
					  fbq('track', 'PageView');`}
        </Script>
    </>
  )
}
